import type { IBreadcrumbItem } from '@/components/Molecules/Breadcrumb/Breadcrumb';
import { HeadMeta } from '@/components/Molecules/HeadMeta/HeadMeta';
import { RelatedSidebar, sidebarContentCardRenderer } from '@/components/Molecules/RelatedSidebar/RelatedSidebar';
import { SaveButton } from '@/components/Molecules/SaveButton/SaveButton';
import { ArticlePageHeader } from '@/components/Organisms/ArticlePageHeader/ArticlePageHeader';
import { ContentTranslation } from '@/components/Organisms/ContentTranslation/ContentTranslation';
import { MachineTranslationDisclaimer } from '@/components/Organisms/ContentTranslation/MachineTranslationDisclaimer';
import { Contributors } from '@/components/Organisms/Contributors/Contributors';
import { RSFViewer } from '@/components/Organisms/RSFViewer/RSFViewer';
import {
    ArticlePageAside,
    ArticlePageMain,
    ArticlePageTemplate,
} from '@/components/Templates/ArticlePageTemplate/ArticlePageTemplate';
import { InfiniteScrollPage } from '@/components/Templates/InfiniteScrollPage/InfiniteScrollPage';
import type { IPageItemFetcher, IPageRendererProps } from '@/components/Templates/InfiniteScrollPage/ItemPageRenderer';
import { SailthruTracking } from '@/hooks/useSailthruTracking';
import useTranslation from '@/hooks/useTranslation';
import { useUserContentViewTracker } from '@/hooks/useUserContentViewTracker';
import type { IAnalystReactionDetail, IContentItem, IFormChoice } from '@/interfaces';
import { ContentType } from '@/interfaces';
import { TrackedProductTypes } from '@/services/Analytics/bnefAnalyticsSettings';
import { BrowserHttpClient } from '@/services/Http/BrowserHttpClient';
import { getAllResearchQuery, SearchContentType } from '@/services/Search';
import type { Translate } from 'next-translate';
import type { FC } from 'react';
import { useId } from 'react';

interface IAnalystReactionsItemProps {
    analystReaction: IAnalystReactionDetail;
    relatedStories: IContentItem[];
    translationLocales?: IFormChoice[];
}

export const analystReactionsBreadcrumbs = (t: Translate): IBreadcrumbItem[] => [
    { url: '/analyst-reactions', label: t('common:Sections.analyst_reactions') },
];

const resultsBuilder = async (initialItem: IContentItem, query?: Record<string, any>): Promise<IContentItem[]> => {
    const resultItems: IContentItem[] = [];
    try {
        const searchResults = await new BrowserHttpClient().search({
            path: '/research_search',
            body: getAllResearchQuery(SearchContentType.AnalystReaction, query),
        });

        if (searchResults?.items) {
            searchResults.items.forEach((item: IContentItem) => {
                if (item.id.toString() !== initialItem.id) {
                    resultItems.push(item);
                }
            });
        }
        return resultItems;
    } catch (e) {
        console.warn('Failed to fetch from /research_search: ' + e.message);
        throw e;
    }
};

export const AnalystReactionsPageAnalyticsInfo = {
    pageName: 'BNEF: Analyst Reactions: Content View',
    siteSection: 'BNEF: Analyst Reactions',
    type: TrackedProductTypes.AnalystReaction,
};

export const analystReactionsPageFetcher: IPageItemFetcher<IAnalystReactionsItemProps> = async (id: string) => {
    const pageData = await new BrowserHttpClient().get({
        path: '/analyst_reactions/[ID]',
        replacements: { id: id.toLowerCase() },
    });
    if (pageData?.analystReaction) {
        SailthruTracking.saveTags(id, pageData.analystReaction.type, pageData.analystReaction.tags);
        return { pageData: pageData as IAnalystReactionsItemProps };
    } else if (pageData?.canonicalResource) {
        return { canonicalResource: pageData.canonicalResource };
    }
    return null;
};
export const AnalystReactionsPageRenderer = ({
    pageData,
    isActive,
}: IPageRendererProps<IAnalystReactionsItemProps>) => {
    useUserContentViewTracker(pageData.analystReaction.id, ContentType.analystreaction, isActive);
    const { t } = useTranslation();
    const disclaimerId = useId();
    return (
        <>
            <ArticlePageHeader
                title={pageData.analystReaction.title}
                date={pageData.analystReaction.displayDate}
                breadCrumbs={analystReactionsBreadcrumbs(t)}
                actions={[
                    <SaveButton
                        key="save"
                        type={pageData.analystReaction.type}
                        id={pageData.analystReaction.id}
                        itemName={pageData.analystReaction.title}
                    />,
                ]}
            />
            <ArticlePageTemplate>
                <ArticlePageMain>
                    <ContentTranslation
                        originalContentLanguage={pageData.analystReaction.originalLanguage}
                        targetDestination={pageData.analystReaction.url}
                        currentTranslationDetails={pageData.analystReaction?.translationDetails ?? null}
                        languageOptions={pageData.translationLocales ?? []}
                        disclaimerId={disclaimerId}
                    />
                    <RSFViewer htmlContent={pageData.analystReaction.body} />
                    <MachineTranslationDisclaimer
                        id={disclaimerId}
                        translationDetails={pageData.analystReaction?.translationDetails}
                    />
                </ArticlePageMain>
                <ArticlePageAside>
                    <RelatedSidebar
                        lists={[
                            pageData.relatedStories.length > 0 && {
                                title: t('Sections.related'),
                                items: pageData.relatedStories,
                                itemRenderer: sidebarContentCardRenderer(
                                    `Related: Automated: Analyst Reaction ${pageData.analystReaction.id}`
                                ),
                            },
                        ]}
                    />
                    {pageData.analystReaction.byPeople?.length > 0 && (
                        <Contributors items={pageData.analystReaction.byPeople} />
                    )}
                </ArticlePageAside>
            </ArticlePageTemplate>
        </>
    );
};

export const AnalystReactionViewPage: FC<IAnalystReactionsItemProps> = (initialData) => {
    return (
        <>
            <HeadMeta
                title={initialData.analystReaction.title}
                type={ContentType.analystreaction}
                image={initialData.analystReaction.leadImage.baseUrl}
                tags={initialData.analystReaction.tags}
            />
            <InfiniteScrollPage
                initialPageData={initialData}
                resultsBuilder={resultsBuilder}
                currentItem={initialData.analystReaction}
            />
        </>
    );
};
